import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { pollMessageStatusThunk } from '../redux/thunks/AILegalMessageThunks';
import { startPolling, stopPolling, updatePollingCheck } from '../redux/slices/simpleLegalProgressSlice';

interface UseMessagePollingResult {
  isPolling: boolean;
  lastCheck: number;
}

/**
 * ✅ Custom hook for message status polling
 * Handles 5-second interval polling with automatic cleanup
 */
export const useMessagePolling = (
  chatId: string | null,
  enabled: boolean = false,
  queryId?: string
): UseMessagePollingResult => {
  const dispatch = useAppDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get polling state for this chat
  const pollingInfo = useAppSelector(state => 
    chatId ? state.simpleLegalProgress.pollingChats[chatId] : null
  );
  
  // ✅ Check MQTT connection status - polling is only a backup when MQTT is not connected
  const isMQTTConnected = useAppSelector(state => state.mqtt.isConnected);

  useEffect(() => {
    
    
    // ✅ Only start polling if enabled, chatId exists, AND MQTT is not connected (polling is backup)
    if (!enabled || !chatId || isMQTTConnected) {
      return;
    }


    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start polling with 5-second interval (backup mechanism for MQTT failures)
    const intervalId = setInterval(async () => {
      try {
        // Update last checked timestamp
        dispatch(updatePollingCheck({ chatId }));
        
        // Poll for message status with queryId for precise matching
        const result = await dispatch(pollMessageStatusThunk({ chatId, queryId })).unwrap();
        
        
        // If polling should stop, clear the interval
        if (!result.shouldContinue) {
          clearInterval(intervalId);
          dispatch(stopPolling({ chatId }));
        }
      } catch (error) {
        console.error(`[POLLING DEBUG] Error during polling:`, error);
        // Continue polling on error (don't stop)
      }
    }, 5000); // 5 seconds - faster polling for backup/recovery scenarios
    

    // Store interval ID for cleanup
    intervalRef.current = intervalId;
    
    // Register polling in Redux state
    dispatch(startPolling({ 
      chatId, 
      messageId: 'unknown', // Will be updated when we know the message ID
      intervalId: intervalId as unknown as number // Type conversion for Redux state
    }));


    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (chatId) {
        dispatch(stopPolling({ chatId }));
      }
    };
  }, [chatId, enabled, queryId, isMQTTConnected, dispatch]);

  return {
    isPolling: !!pollingInfo,
    lastCheck: pollingInfo?.lastChecked || 0
  };
};

export default useMessagePolling;
